import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPublishedAssessmentByCourseId } from "action/assessment";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Container,
  Row,
  Col,
  Button,
  UncontrolledTooltip,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import CommonModal from "components/CommonComponents/CommonModal";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import { format } from "date-fns";
import Select, { components } from "react-select";
import {
  showAlert,
  toTitleCase,
} from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import {
  SELECT_CUSTOM_STYLES,
  fixedWidthCustomStyle,
  MAX_DOCUMENT_SIZE,
} from "constants/CommonConstants";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
// import { getCourseById } from "action/course";
import { useParams, useLocation } from "react-router-dom";
import {
  urlDecodedId,
  urlEncodedId,
} from "components/CommonComponents/encodeDecodeFunction";
import DatePicker from "react-datepicker";
import {
  createAssessmentArtifact,
  updateAssessmentArtifact,
  getAssessmentArtifactList,
  getAssessmentArtifactById,
  deleteAssessmentArtifactFile,
} from "action/assessmentArtifact";

const ARTIFACT_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
];

const getInitialForm = () => ({
  id: null,
  assessment: {
    value: null,
    error: "",
  },
  marks: {
    value: "",
    error: "",
  },
  status: {
    value: "PENDING",
    error: "",
  },
  docRows: [getInitialDocRow()],
  existingFiles: [],
});

const getInitialDocRow = () => ({
  file: { value: null, error: "" },
  fileName: "",
});

const CourseArtifacts = ({
  isEmbedded = false,
  embeddedCourseId,
  embeddedBatchId,
  embeddedTraineeID,
}) => {
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const { id } = useParams();
  const location = useLocation();

  const decodedId = embeddedCourseId ? urlDecodedId(embeddedCourseId) : (id ? urlDecodedId(id) : null);

  const urlSearchParams = new URLSearchParams(location.search);
  const locationState = location.state || {};
  const batchIdParam = isEmbedded
    ? embeddedBatchId
    : (locationState.batchId || urlSearchParams.get("batchId"));

  const decodedBatchId = batchIdParam
    ? /^\d+$/.test(String(batchIdParam))
      ? batchIdParam
      : urlDecodedId(batchIdParam)
    : null;

  const traineeID = isEmbedded
    ? embeddedTraineeID
    : (locationState.traineeId || locationState.traineeID || urlSearchParams.get("traineeID"));

  // const [courseData, setCourseData] = useState(null);
  // const [courseLoading, setCourseLoading] = useState(true);
  // const [showImageModal, setShowImageModal] = useState(false);

  const [filterState, setFilterState] = useState({
    searchName: "",
    status: null,
    completionDate: [null, null],
    filterParams: [],
  });

  const [docsModal, setDocsModal] = useState({
    isOpen: false,
    docs: [],
    selectedDoc: null,
    previewLoading: false,
  });

  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(getInitialForm());

  const assessmentListData = useSelector(
    (state) => state.assessment?.coursePublishedAssessmentList,
  );

  const assessmentArtifactListData = useSelector(
    (state) => state.assessment?.assessmentArtifactList,
  );



  const assessmentOptions =
    assessmentListData?.data?.assessments?.map((assessment) => ({
      value: assessment.ID,
      label: assessment.assessmentName,
      canAdd: assessment.canAdd,
      isDisabled: assessment.canAdd === false,
    })) || [];



  const buildFilterParams = (currentFilterState = filterState) => {
    const filters = [];

    if (currentFilterState.searchName?.trim()) {
      filters.push({
        field: "AssessmentName",
        condition: "like",
        filterValues: [currentFilterState.searchName.trim()],
      });
    }

    if (currentFilterState.status?.value) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [currentFilterState.status.value],
      });
    }

    if (currentFilterState.completionDate[0]) {
      if (currentFilterState.completionDate[1]) {
        filters.push({
          field: "CompletionDate",
          condition: "btw",
          filterValues: [
            format(currentFilterState.completionDate[0], "yyyy-MM-dd"),
            format(currentFilterState.completionDate[1], "yyyy-MM-dd"),
          ],
        });
      } else {
        filters.push({
          field: "CompletionDate",
          condition: "eq",
          filterValues: [
            format(currentFilterState.completionDate[0], "yyyy-MM-dd"),
          ],
        });
      }
    }

    return filters.length > 0 ? JSON.stringify(filters) : null;
  };

  const fetchArtifacts = (
    currentPage = pageNum,
    currentFilterState = filterState,
  ) => {
    const filtersJSON = buildFilterParams(currentFilterState);

    getAssessmentArtifactList(dispatch, currentPage, filtersJSON, decodedId, decodedBatchId, traineeID);
    getPublishedAssessmentByCourseId(dispatch, decodedId, traineeID);
  };

  useEffect(() => {
    fetchArtifacts();
  }, [decodedId, decodedBatchId, traineeID, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (assessmentArtifactListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          assessmentArtifactListData?.data?.assessmentArtifact?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      assessmentArtifactListData?.code ||
      assessmentArtifactListData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          assessmentArtifactListData?.message ||
          "Failed to load artifacts! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [assessmentArtifactListData]);

  // const fetchCourseData = async (id) => {
  //   try {
  //     setCourseLoading(true);

  //     const res = await getCourseById(id);

  //     if (res?.code === 200) {
  //       setCourseData(res.data);
  //     } else {
  //       notify("danger", res?.message || "Failed to load course details!");
  //     }
  //   } catch (error) {
  //     notify(
  //       "danger",
  //       error?.message || "Something went wrong while fetching course details",
  //     );
  //   } finally {
  //     setCourseLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   if (!decodedId) return;

  //   fetchCourseData(decodedId);
  // }, [decodedId]);

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    const defaultFilters = {
      searchName: "",
      status: null,
      completionDate: [null, null],
      filterParams: [],
    };

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setFilterState(defaultFilters);

    if (pageNum !== 1) {
      setPageNum(1);
    } else {
      fetchArtifacts(1, defaultFilters);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const openDocsModal = (docs, selectedDocId = null) => {
    const selected = selectedDocId
      ? docs.find((d) => d.id === selectedDocId)
      : docs?.[0] || null;
    setDocsModal({
      isOpen: true,
      docs,
      selectedDoc: selected,
      previewLoading: !!selected,
    });
  };
  const closeDocsModal = () =>
    setDocsModal({
      isOpen: false,
      docs: [],
      selectedDoc: null,
      previewLoading: false,
    });
  const selectDocForPreview = (doc) => {
    setDocsModal((prev) => ({
      ...prev,
      selectedDoc: doc,
      previewLoading: true,
    }));
  };
  const handlePreviewLoad = () =>
    setDocsModal((prev) => ({ ...prev, previewLoading: false }));

  const openAddPanel = () => {
    setFormData(getInitialForm());
    setIsPanelOpen(true);
  };

  const openEditPanel = async (row) => {
    try {
      setIsPanelOpen(true);
      setListState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const res = await getAssessmentArtifactById(row.id);

      if (res?.code === 200) {
        const data = res.data;

        const mappedFiles = (data.files || []).map((file) => {
          const filePath = file.filePath || "";
          const ext = filePath.split("?")[0].split(".").pop()?.toLowerCase();

          const fileType = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)
            ? "image"
            : ["mp4", "webm", "ogg"].includes(ext)
              ? "video"
              : ext === "pdf"
                ? "pdf"
                : "";

          return {
            ...file,
            name: filePath.split("/").pop() || "Document",
            previewUrl: filePath.startsWith("http") ? filePath : `/${filePath}`,
            fileType,
          };
        });

        setFormData({
          id: row.id,
          assessment: {
            value: { value: data.assessmentID, label: data.assessmentName },
            error: "",
          },
          marks: {
            value: String(data.marks || ""),
            error: "",
          },
          status: {
            value: data.status || "PENDING",
            error: "",
          },
          docRows: [getInitialDocRow()],
          existingFiles: mappedFiles,
        });
      } else {
        setIsPanelOpen(false);
        notify("danger", res?.message || "Failed to load artifact details");
      }
    } catch (error) {
      notify("danger", "Failed to load artifact details");
      setIsPanelOpen(false);
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setFormData(getInitialForm());
  };

  const handleDeleteFile = (fileId) => {
    showAlert({
      title: "Are you sure you want to delete this document?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await deleteAssessmentArtifactFile(formData.id, fileId);
          if (res?.code === 200) {
            notify("success", "File deleted successfully");
            setFormData((prev) => ({
              ...prev,
              existingFiles: prev.existingFiles.filter(
                (file) => file.id !== fileId,
              ),
            }));
            fetchArtifacts();
          } else {
            notify("danger", res?.message || "Failed to delete file");
          }
        } catch (err) {
          notify("danger", "Failed to delete file");
        }
      },
    });
  };

  const getValidationError = (name, value) => {
    // const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "assessment":
        return !value ? "Please select an assessment" : "";
      case "marks":
        return !value ? "Please enter valid marks" : "";
      case "documentFile":
        if (!value) return "Please upload document";

        if (
          value.type !== "application/pdf" &&
          !value.type.startsWith("image/")
        ) {
          return "Only PDF and image files are allowed";
        }

        if (value.size > MAX_DOCUMENT_SIZE) {
          return "File size must not exceed 15 MB";
        }

        return "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value, index = null) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);

    if (name === "assessment" || name === "marks") {
      setFormData((prev) => ({
        ...prev,
        [name]: {
          value: trimmedValue,
          error,
        },
      }));
    } else if (name === "documentFile" && index !== null) {
      setFormData((prev) => ({
        ...prev,
        docRows: prev.docRows.map((row, i) =>
          i === index
            ? {
              ...row,
              file: {
                value: trimmedValue,
                error,
              },
            }
            : row,
        ),
      }));
    }
  };

  const handleSave = async () => {
    let hasError = false;
    const newForm = {
      ...formData,
      assessment: { ...formData.assessment },
      docRows: formData.docRows.map((row) => ({
        ...row,
        file: { ...row.file },
      })),
    };

    if (!newForm.assessment.value) {
      newForm.assessment.error = "Please select an assessment";
      hasError = true;
    }

    const validDocRows = newForm.docRows.filter((row) => row.file.value);

    if (validDocRows.length === 0 && newForm.existingFiles.length === 0) {
      newForm.docRows[0].file.error = "Please upload document";
      hasError = true;
    }

    setFormData(newForm);

    if (hasError) return;

    setIsSaving(true);
    try {
      const data = new FormData();

      data.append("assessmentID", newForm.assessment.value.value);
      if (decodedId) data.append("courseID", decodedId);
      if (batchIdParam) data.append("batchID", batchIdParam);
      if (embeddedTraineeID) {
        data.append("traineeID", embeddedTraineeID);
      }
      data.append("status", newForm.status.value);

      validDocRows.forEach((docRow) => {
        data.append("file", docRow.file.value);
      });

      let res;
      if (formData.id) {
        res = await updateAssessmentArtifact(formData.id, data);
      } else {
        res = await createAssessmentArtifact(data);
      }

      if (res?.code === 200) {
        notify(
          "success",
          `Artifact ${formData.id ? "updated" : "added"} successfully`,
        );
        closePanel();
        fetchArtifacts();
      } else {
        notify(
          "danger",
          res?.message ||
          `Failed to ${formData.id ? "update" : "add"} artifact`,
        );
      }
    } catch (error) {
      notify("danger", "An error occurred while saving the artifact");
    } finally {
      setIsSaving(false);
    }
  };

  const addDocRow = () =>
    setFormData((prev) => ({
      ...prev,
      docRows: [...prev.docRows, getInitialDocRow()],
    }));

  const removeDocRow = (index) =>
    setFormData((prev) => ({
      ...prev,
      docRows: prev.docRows.filter((_, i) => i !== index),
    }));

  const handleFileChange = (index, file) => {
    const error = getValidationError("documentFile", file);
    setFormData((prev) => ({
      ...prev,
      docRows: prev.docRows.map((row, i) =>
        i === index
          ? {
            ...row,
            file: { value: error ? null : file, error: error },
            fileName: error ? "" : (file?.name || ""),
          }
          : row,
      ),
    }));
  };

  const clearFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      docRows: prev.docRows.map((row, i) =>
        i === index
          ? {
            ...row,
            file: { value: null, error: "" },
            fileName: "",
          }
          : row,
      ),
    }));
  };

  // const handleBack = () => {
  //   if (id) {
  //     navigate(`/admin/course/${id}/details?tab=enrollment`, {
  //       state: { ...location.state }
  //     });
  //   }
  // };

  const tableData = (
    assessmentArtifactListData?.data?.assessmentArtifact || []
  ).map((artifact, index) => ({
    srNo: (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE + index + 1,
    id: artifact.id,
    assessmentName: artifact.assessmentName,
    marks: artifact.marks,
    completionDate: artifact.completionDate,
    status: artifact.status,
    files: (artifact.files || artifact.documents || []).map((file) => {
      const filePath = file.filePath || "";
      const ext = filePath.split("?")[0].split(".").pop()?.toLowerCase();
      const fileType = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)
        ? "image"
        : ["mp4", "webm", "ogg"].includes(ext)
          ? "video"
          : ext === "pdf"
            ? "pdf"
            : "";
      return {
        ...file,
        name: filePath.split("/").pop() || "Document",
        previewUrl: filePath.startsWith("http") ? filePath : `/${filePath}`,
        fileType,
      };
    }),
    _raw: artifact,
  }));

  const Option = (props) => (
    <components.Option {...props}>
      <Badge
        color={props.data.value === "PENDING" ? "warning" : "success"}
        pill
      >
        {props.data.label}
      </Badge>
    </components.Option>
  );
  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge
        color={props.data.value === "PENDING" ? "warning" : "success"}
        pill
      >
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  const renderDocPreview = (doc) => {
    if (!doc) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100">
          Select a document to preview
        </div>
      );
    }

    const url = doc.previewUrl || "";
    const ext = url.split("?")[0].split("#")[0].split(".").pop()?.toLowerCase();

    const fileType =
      doc.fileType ||
      (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)
        ? "image"
        : ["mp4", "webm", "ogg"].includes(ext)
          ? "video"
          : ext === "pdf"
            ? "pdf"
            : "");

    switch (fileType) {
      case "image":
        return (
          <img
            src={url}
            alt={doc.name}
            className="preview-image"
            onLoad={handlePreviewLoad}
          />
        );

      case "video":
        return (
          <video
            controls
            className="preview-video"
            onLoadedData={handlePreviewLoad}
          >
            <source src={url} />
            Your browser does not support the video tag.
          </video>
        );

      case "pdf":
        return (
          <iframe
            src={url}
            title={doc.name}
            className="preview-frame"
            onLoad={handlePreviewLoad}
          />
        );

      default:
        return (
          <div className="d-flex justify-content-center align-items-center h-100">
            Preview not available
          </div>
        );
    }
  };

  const columns = [
    {
      Header: "Sr.No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 70,
      maxWidth: 70,
    },
    {
      Header: "Assessment Name",
      accessor: "assessmentName",
      minWidth: 180,
      maxWidth: 180,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Completion Date",
      accessor: "completionDate",
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }) => <div className="text-wrapper">{value || "-"}</div>,
    },
    {
      Header: "Documents",
      accessor: "files",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ value, row }) => {
        const encodedRowId = urlEncodedId(row.original.id);
        return (
          <>
            <Button
              color="link"
              className="p-0"
              id={`docs-${encodedRowId}`}
              onClick={() => openDocsModal(value)}
            >
              <i className="fa fa-paperclip" />
              <span className="ml-1">{value?.length || 0}</span>
            </Button>
            <UncontrolledTooltip delay={0} target={`docs-${encodedRowId}`}>
              View Documents
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ value }) => (
        <Badge color={value === "PENDING" ? "warning" : "success"} pill>
          {toTitleCase(value)}
        </Badge>
      ),
    },
    {
      Header: "Action",
      accessor: "_raw",
      minWidth: 140,
      maxWidth: 140,
      Cell: ({ value: row }) => {
        const encodedRowId = urlEncodedId(row.id);
        const isPending = row.status === "PENDING";

        if (!isPending) {
          return "-";
        }

        return (
          <div className="d-flex align-items-center">
            {/* Edit */}
            {isPending && (
              <>
                <Button
                  color="link"
                  className="p-0"
                  id={`artifactEdit-${encodedRowId}`}
                  onClick={() => openEditPanel(row)}
                >
                  <i className="fas fa-edit m-0 ml-1" />
                </Button>
                <UncontrolledTooltip
                  delay={0}
                  placement="top"
                  target={`artifactEdit-${encodedRowId}`}
                >
                  Edit Artifact
                </UncontrolledTooltip>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const content = (
    <>
      {/* <Card className="course-hero-card mb-4">
          <CardBody className="card-body-custom">
            <Row className="align-items-center">
              <Col lg="8">
                <div className="course-description-wrapper">
                  <div className="course-header-top">
                    <div className="course-header-left">
                      <button
                        className="td-back-btn"
                        onClick={handleBack}
                        title="Back to Course"
                      >
                        <i className="fa fa-arrow-left" />
                      </button>

                      {courseData?.courseType && (
                        <span
                          className={`radio-badge ${
                            courseData.courseType?.toLowerCase() === "live"
                              ? "live"
                              : "recorded"
                          }`}
                        >
                          <span className="radio-circle">
                            <span className="radio-dot" />
                          </span>
                          {courseData.courseType}
                        </span>
                      )}
                    </div>
                  </div>

                  {courseLoading ? (
                    <div className="mt-3">
                      <i className="fa fa-spinner fa-spin text-muted" />
                    </div>
                  ) : (
                    <h2 className="course-title mt-3 mb-2">
                      {courseData?.courseName || "Course Artifacts"}
                    </h2>
                  )}

                  <div className="course-info-bar mt-4">
                    {courseData?.level && (
                      <div className="info-item">
                        <i className="fa-solid fa-chart-simple info-icon"></i>
                        <div>
                          <h4 className="text-default mb-0">
                            {toTitleCase(courseData.level)}
                          </h4>
                          <p className="small-text mb-0">Level</p>
                        </div>
                      </div>
                    )}

                    {courseData?.totalEnrolledUsers !== undefined && (
                      <div className="info-item">
                        <i className="fa fa-users info-icon"></i>
                        <div>
                          <h4 className="text-default mb-0">
                            {courseData.totalEnrolledUsers}
                          </h4>
                          <p className="small-text mb-0">Enrolled</p>
                        </div>
                      </div>
                    )}

                    {traineeName && (
                      <div className="info-item">
                        <i className="fa fa-user info-icon"></i>
                        <div>
                          <h4
                            className="text-default mb-0 text-truncate course-info-truncate"
                            id="traineeNameTooltip"
                          >
                            {traineeName}
                          </h4>
                          <p className="small-text mb-0">Trainee</p>
                          {traineeName.length > 15 && (
                            <UncontrolledTooltip
                              delay={0}
                              target="traineeNameTooltip"
                              placement="bottom"
                            >
                              {traineeName}
                            </UncontrolledTooltip>
                          )}
                        </div>
                      </div>
                    )}

                    {batchName && (
                      <div className="info-item">
                        <i className="fa fa-calendar-check info-icon"></i>
                        <div>
                          <h4
                            className="text-default mb-0 text-truncate course-info-truncate"
                            id="batchNameTooltip"
                          >
                            {batchName}
                          </h4>
                          <p className="small-text mb-0">Batch</p>
                          {batchName.length > 15 && (
                            <UncontrolledTooltip
                              delay={0}
                              target="batchNameTooltip"
                              placement="bottom"
                            >
                              {batchName}
                            </UncontrolledTooltip>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>

              <Col lg="4">
                <div className="course-image-section">
                  <div
                    className="hero-image-wrapper cursor"
                    onClick={() =>
                      courseData?.thumbnailPath && setShowImageModal(true)
                    }
                    title={
                      courseData?.thumbnailPath ? "Click to preview" : undefined
                    }
                  >
                    <img
                      src={
                        courseData?.thumbnailPath || "/course-placeholder.png"
                      }
                      alt={courseData?.courseName || "Course"}
                      className="hero-image"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card> */}

      <Card className={isEmbedded ? "mt-3" : ""}>
        <CardHeader className="d-flex align-items-center justify-content-end">
          <Button
            color="default"
            className="btn-deep-blue d-flex align-items-center"
            onClick={openAddPanel}
          >
            <i className="fa fa-plus mr-1" />
            <span>Add Artifact</span>
          </Button>
        </CardHeader>

        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
            <div className="d-flex flex-wrap align-items-center filter-group">
              {/* Search */}
              <Input
                type="text"
                placeholder="Search by assessment name"
                className="mr-2 mb-2 filter-input-large"
                value={filterState.searchName}
                onChange={(e) =>
                  setFilterState((prev) => ({
                    ...prev,
                    searchName: e.target.value,
                  }))
                }
                onKeyDown={handleKeyDown}
              />

              {/* Completion Date */}
              <DatePicker
                selected={filterState.completionDate[0]}
                onChange={(dates) =>
                  setFilterState((prev) => ({
                    ...prev,
                    completionDate: dates,
                  }))
                }
                startDate={filterState.completionDate[0]}
                endDate={filterState.completionDate[1]}
                selectsRange
                isClearable={!!filterState.completionDate[0]}
                placeholderText="Select completion date or range"
                className="form-control mr-2 mb-2 filter-input-large"
                dateFormat="dd-MMM-yyyy"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                calendarClassName="custom-calendar"
                onKeyDown={handleKeyDown}
              />

              {/* Status */}
              <Select
                className="mr-2 mb-2 filter-select"
                options={ARTIFACT_STATUS_OPTIONS}
                value={filterState.status}
                classNamePrefix="react-select"
                onChange={(selected) =>
                  setFilterState((prev) => ({
                    ...prev,
                    status: selected,
                  }))
                }
                placeholder="Select status"
                styles={fixedWidthCustomStyle()}
                components={{ Option, SingleValue }}
                onKeyDown={handleKeyDown}
              />

              {/* Search */}
              <Button
                color="default"
                onClick={handleSearch}
                className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""
                  }`}
              >
                {listState.showLoaderOnClick ? (
                  <i className="fa fa-spinner fa-spin loader" />
                ) : (
                  <>
                    <i className="fa fa-search mr-1" />
                    Search
                  </>
                )}
              </Button>

              {/* Clear */}
              {filterState.filterParams.length > 0 && (
                <Button
                  color="default"
                  className="mb-2 filter-btn btn-simple btn-deep-blue"
                  onClick={handleClear}
                >
                  <i className="fa fa-times mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="mt-2 mb-3 mx-1">
            {assessmentArtifactListData?.data?.totalRecords > 0 ? (
              <>
                {filterState.filterParams.length > 0 ? (
                  <span className="total-records">
                    Showing{" "}
                    {assessmentArtifactListData?.data?.totalFilteredRecords} of{" "}
                    {assessmentArtifactListData?.data?.totalRecords} Records
                  </span>
                ) : (
                  <span className="total-records">
                    Total Records :{" "}
                    {assessmentArtifactListData?.data?.totalRecords}
                  </span>
                )}
              </>
            ) : null}
          </div>

          {listState.isLoading ? (
            <div className="loaderContainer">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          ) : listState.error ? (
            <div className="loaderContainer error-style">
              <div className="error-icon">
                <i className="fa-solid fa-circle-exclamation" />
              </div>
              <span>{listState.error}</span>
            </div>
          ) : listState.noRecords ? (
            <div className="loaderContainer empty-style">
              <div className="empty-icon">
                <i className="fa-solid fa-circle-info" />
              </div>
              <span>No artifacts found.</span>
            </div>
          ) : (
            <CommonTable
              columns={columns}
              data={tableData}
              defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
              activePage={pageNum}
              setPageNum={setPageNum}
              totalPages={Math.ceil(
                assessmentArtifactListData?.data?.totalFilteredRecords /
                assessmentArtifactListData?.data?.noOfRecordsPerPage ||
                DEFAULT_RECORDS_PER_PAGE,
              )}
              showPaginationBottom={true}
            />
          )}
        </CardBody>
      </Card>

      <CommonModal
        isOpen={docsModal.isOpen}
        toggle={closeDocsModal}
        title="Documents"
        size="xl"
      >
        {!docsModal.docs || docsModal.docs.length === 0 ? (
          <div className="text-center text-muted my-5">
            <div className="empty-icon mb-2">
              <i className="fa-regular fa-folder-open fa-2x" />
            </div>
            <span>No documents found.</span>
          </div>
        ) : (
          <Row className="artifact-container">
            {docsModal.docs.length > 1 && (
              <Col xs="12" md="2" className="artifact-left">
                <div className="artifact-list">
                  {docsModal.docs.map((doc) => {
                    const isImage =
                      doc.fileType === "image" ||
                      (doc.name && doc.name.match(/\.(jpg|jpeg|png|gif)$/i));

                    return (
                      <div
                        key={doc.id}
                        className={`artifact-document-card ${docsModal.selectedDoc?.id === doc.id
                          ? "selected-artifact-document"
                          : ""
                          }`}
                        onClick={() => selectDocForPreview(doc)}
                      >
                        <div className="artifact-document-thumbnail">
                          {isImage ? (
                            <img
                              src={doc.previewUrl}
                              alt={doc.name}
                              className="thumbnail-image"
                            />
                          ) : (
                            <div className="file-thumbnail">
                              <i
                                className={`fas lessonIcon ${doc.fileType === "pdf"
                                  ? "fa-file-pdf"
                                  : doc.icon || "fa-file"
                                  }`}
                              />
                            </div>
                          )}
                        </div>

                        <div className="artifact-document-divider" />

                        <div className="artifact-document-info">
                          <div className="artifact-document-date">
                            {format(
                              new Date(doc.createdAt || new Date()),
                              "dd MMM yyyy, hh:mm a",
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Col>
            )}

            <Col
              xs="12"
              md={docsModal.docs.length > 1 ? "10" : "12"}
              className="artifact-right"
            >
              <div className="artifact-preview">
                {docsModal.previewLoading && (
                  <div className="preview-loader">
                    <i className="fa fa-spinner fa-spin loader fa-2x text-default" />
                  </div>
                )}

                {renderDocPreview(docsModal.selectedDoc)}
              </div>
            </Col>
          </Row>
        )}
      </CommonModal>

      <RightPanelCard
        isOpen={isPanelOpen}
        enableScroll={true}
        onClose={closePanel}
      >
        <Card className="h-100 d-flex flex-column">
          <CardHeader tag="div" className="p-3 flex-shrink-0">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={closePanel}
                />
                <div className="d-flex align-items-center">
                  <h4 className="mb-0 mr-2">
                    {formData.id ? "Edit Artifact" : "Add Artifact"}
                  </h4>
                  <span className="td-upload-format-hint mb-1">
                    ( Accepted formats: PDF, JPG, JPEG, PNG - Max 15 MB )
                  </span>
                </div>
              </div>
              <i
                className="fa-solid fa-xmark cursor text-muted"
                onClick={closePanel}
              />
            </div>
          </CardHeader>

          <CardBody className="td-upload-card-body flex-grow-1">
            {listState.isLoading ? (
              <div className="loaderContainer h-100 d-flex align-items-center justify-content-center">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : (
              <>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>
                        Assessment <span className="text-danger">*</span>
                      </Label>
                      <Select
                        options={assessmentOptions}
                        value={formData.assessment.value}
                        onChange={(opt) => {
                          setFormData((prev) => ({
                            ...prev,
                            assessment: { value: opt, error: "" },
                          }));
                        }}
                        onBlur={() =>
                          handleOnBlur("assessment", formData.assessment.value)
                        }
                        placeholder="Select assessment"
                        styles={SELECT_CUSTOM_STYLES}
                        classNamePrefix="react-select"
                        className={
                          formData.assessment.error ? "is-invalid" : ""
                        }
                        isClearable
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                      />
                      {formData.assessment.error && (
                        <div className="invalid-feedback d-block">
                          {formData.assessment.error}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                {formData.id && formData.existingFiles.length > 0 && (
                  <Label className="td-field-label">Documents</Label>
                )}
                {formData.id && formData.existingFiles.length > 0 && (
                  <div className="existing-files-section mb-3">
                    <div className="d-flex flex-wrap">
                      {formData.existingFiles.map((file) => {
                        const filename =
                          file.filePath?.split("/").pop() || "Document";
                        const fileUrl = file.filePath?.startsWith("http")
                          ? file.filePath
                          : `/${file.filePath}`;
                        const isPdf = file.filePath
                          ?.split("?")[0]
                          .toLowerCase()
                          .endsWith(".pdf");

                        return (
                          <div
                            key={file.id}
                            className="existing-file-item position-relative mr-3 mb-3"
                          >
                            <div
                              className="existing-file-preview w-100 h-100 border rounded overflow-hidden d-flex flex-column align-items-center justify-content-center bg-light cursor-pointer"
                              onClick={() => openDocsModal([file])}
                            >
                              {isPdf ? (
                                <>
                                  <i className="fa fa-file-pdf text-danger fa-2x" />
                                </>
                              ) : (
                                <img
                                  src={fileUrl}
                                  alt={filename}
                                  className="existing-file-image"
                                />
                              )}
                            </div>

                            <Button
                              color="secondary"
                              className="existing-file-delete position-absolute rounded-circle p-0 d-flex align-items-center justify-content-center border-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFile(file.id);
                              }}
                              title="Delete file"
                            >
                              <i className="fa fa-times" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Label className="td-field-label">Upload New Documents</Label>
                <div className="document-section w-100">
                  {formData.docRows.map((row, index) => {
                    const totalDocs = formData.docRows.length;
                    return (
                      <div key={index} className="td-upload-row">
                        <div className="td-upload-serial">
                          <span className="td-upload-serial-num mt-1">
                            {index + 1}
                          </span>
                        </div>

                        <div className="td-upload-field">
                          <FormGroup className="mb-0">
                            <Label className="td-field-label">
                              Upload Document{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <div
                              className={`td-file-picker ${row.file.error ? "td-file-picker--invalid" : ""
                                }`}
                            >
                              <Label className="td-browse-btn mb-0">
                                <i className="fas fa-folder-open mr-1" /> Browse
                                <Input
                                  type="file"
                                  hidden
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  id={`artifactDocFile-${index}`}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    handleFileChange(index, file);
                                    e.target.value = "";
                                  }}
                                  onBlur={() =>
                                    handleOnBlur(
                                      "documentFile",
                                      row.file.value,
                                      index,
                                    )
                                  }
                                />
                              </Label>
                              <div className="td-file-info">
                                <i className="fa fa-file-pdf-o td-file-pdf-icon" />
                                <span
                                  className={`td-file-name ${!row.file.value
                                    ? "td-file-name--placeholder"
                                    : ""
                                    }`}
                                >
                                  {row.fileName || "No file chosen"}
                                </span>
                                {row.file.value && (
                                  <i
                                    className="fa fa-times td-file-remove"
                                    onClick={() => clearFile(index)}
                                    title="Remove file"
                                  />
                                )}
                              </div>
                            </div>
                            {row.file.error && (
                              <div className="invalid-feedback d-block">
                                {row.file.error}
                              </div>
                            )}
                          </FormGroup>
                        </div>

                        <div className="td-upload-actions">
                          {totalDocs > 1 && (
                            <button
                              className="td-row-action-btn td-row-action-btn--remove"
                              onClick={() => removeDocRow(index)}
                              title="Remove row"
                            >
                              <i className="fa fa-times" />
                            </button>
                          )}
                          {index === totalDocs - 1 && (
                            <button
                              className="td-row-action-btn td-row-action-btn--add mt-1"
                              onClick={addDocRow}
                              title="Add another document"
                            >
                              <i className="fa fa-plus" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Col md="12">
                  <FormGroup>
                    <div className="mt-5">
                      <Label className="d-flex align-items-center cursor mb-0">
                        <Input
                          type="checkbox"
                          checked={formData.status.value === "COMPLETED"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: {
                                value: e.target.checked
                                  ? "COMPLETED"
                                  : "PENDING",
                                error: "",
                              },
                            }))
                          }
                        />
                        <span className="text-muted ml-2 mt-1">
                          By confirming, this artifact will be locked and cannot
                          be edited or deleted.
                        </span>
                      </Label>
                    </div>
                  </FormGroup>
                </Col>
              </>
            )}
          </CardBody>

          {!listState.isLoading && (
            <CardFooter className="text-right flex-shrink-0">
              <Button
                color="secondary"
                className="save-btn-sm mr-2"
                onClick={closePanel}
              >
                Cancel
              </Button>
              <Button
                color="deep-blue"
                className="save-btn-sm"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <i className="fa fa-spinner fa-spin loader ml-3" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          )}
        </Card>
      </RightPanelCard>

      {/* {showImageModal && courseData?.thumbnailPath && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div className="image-modal-content">
            <img src={courseData.thumbnailPath} alt="course-thumbnail" />
          </div>
        </div>
      )} */}
    </>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        {content}
      </Container>
    </>
  );
};

export default CourseArtifacts;
